// src/utils/h2figDecryptor.ts
import { getLogger } from "../helpers/logger";
import { sha256 } from "@noble/hashes/sha2.js";
import { bytesToHex, utf8ToBytes } from "@noble/hashes/utils.js";
import { H2FIG_HEADER, keyFor, xorTransform } from "./h2fig-format";

const decryptLogger = getLogger("Decryptor");

/**
 * Standalone H2FIG decryptor utility
 * Based on the working example from paste.txt
 */


interface DecryptionOptions {
  skipIntegrityCheck?: boolean;
}

interface H2FigStructure {
  signature: string;
  keyHint: string;
  data: string;
  format: string;
}


/**
 * SHA-256 of the decrypted payload, for the integrity signature.
 *
 * @noble/hashes — the same call this codebase already makes for the abuse fingerprint, the
 * capture cache key and the extension pairing code. It replaces three things at once:
 *
 *  - `crypto.subtle.digest` plus a hand-written hex loop;
 *  - a probe for whether Web Crypto exists at all, which a synchronous pure-JS hash makes moot;
 *  - and `fallbackHash`, a 32-bit djb2 zero-padded to 64 characters "to look like SHA-256". It
 *    could never equal the real signature, so wherever it ran the integrity check was not
 *    lenient — it was guaranteed to mismatch, and the warning it logged said the payload was
 *    corrupt when nothing was wrong with it.
 */
function generateHash(text: string): string {
  return bytesToHex(sha256(utf8ToBytes(text)));
}

// Main Decryption Function
export async function decryptH2Fig(
  h2figContent: ArrayBuffer | string,
  options: DecryptionOptions = { skipIntegrityCheck: false },
): Promise<any> {
  try {
    let fileBuffer;

    // Step 1: Standardize Input to ArrayBuffer
    if (typeof h2figContent === "string") {
      throw new Error("String input not supported, please provide ArrayBuffer");
    } else if (h2figContent instanceof ArrayBuffer) {
      fileBuffer = h2figContent;
    } else {
      throw new Error("Invalid input type. Expected ArrayBuffer.");
    }

    // Create a Uint8Array view for easier byte manipulation
    const fileBytes = new Uint8Array(fileBuffer);

    // Step 2: Verify H2FIG Header
    if (fileBytes.length < H2FIG_HEADER.length) {
      throw new Error(
        `Invalid H2FIG file: Too short (${fileBytes.length} bytes).`,
      );
    }

    // Check header bytes
    const headerFromFile = fileBytes.slice(0, H2FIG_HEADER.length);
    for (let i = 0; i < H2FIG_HEADER.length; i++) {
      if (headerFromFile[i] !== H2FIG_HEADER[i]) {
        throw new Error(`Invalid H2FIG file: Header mismatch at byte ${i}.`);
      }
    }

    // Step 3: Extract JSON Payload after header
    const jsonPayloadBytes = fileBytes.slice(H2FIG_HEADER.length);

    // Step 4: Decode UTF-8 and Parse JSON
    let finalData: H2FigStructure;
    try {
      const outerJsonString = new TextDecoder("utf-8").decode(jsonPayloadBytes);
      finalData = JSON.parse(outerJsonString);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      throw new Error("Failed to decode H2FIG structure: " + errorMessage);
    }

    // Step 5: Validate structure
    if (
      !finalData ||
      typeof finalData !== "object" ||
      !finalData.signature ||
      !finalData.keyHint ||
      !finalData.data ||
      finalData.format !== "h2fig"
    ) {
      throw new Error("Invalid H2FIG structure: Missing required fields");
    }

    // Step 6: Extract encryption parameters
    const encryptedDataString = finalData.data;
    const dateKey = finalData.keyHint;
    const originalSignature = finalData.signature;

    // Validate date format
    if (!/^\d{4}-\d{2}-\d{2}$/.test(dateKey)) {
      throw new Error(`Invalid date format in keyHint: "${dateKey}"`);
    }

    // Step 7: Reconstruct key and decrypt
    const decryptedJsonString = xorTransform(encryptedDataString, keyFor(dateKey));

    // Step 8: Verify integrity — ADVISORY, not enforced.
    //
    // Naming this `skipIntegrityCheck: false` reads like a strict mode, and it is not one: a
    // mismatch is logged and the payload is imported anyway. Measured, not inferred — a file
    // built with a signature of 64 `f`s imports cleanly through this path.
    //
    // Left advisory deliberately. The signature is a plain digest, not a MAC — the XOR key is
    // derived from a date stored in the file itself, so anyone rewriting the payload can rewrite
    // the signature too, and enforcing it would buy no security. What it can catch is CORRUPTION
    // (a truncated download, a mangled clipboard round trip), and there the next step already
    // fails loudly: `JSON.parse` on damaged bytes throws. Rejecting here instead would also make
    // every file written by an encoder whose digest disagrees with this one unimportable, and the
    // browser extension's encoder lives outside this repository, so that agreement cannot be
    // checked from here.
    if (!options.skipIntegrityCheck) {
      try {
        const calculatedHash = generateHash(decryptedJsonString);
        if (calculatedHash !== originalSignature) {
          decryptLogger.warn("H2FIG Integrity Warning: Hash mismatch");
          decryptLogger.warn("Expected:", originalSignature);
          decryptLogger.warn("Calculated:", calculatedHash);
          // Continue despite mismatch - don't throw error
        }
      } catch (hashError) {
        const errorMessage = hashError instanceof Error ? hashError.message : String(hashError);
        decryptLogger.warn("Integrity check skipped:", errorMessage);
        // Continue despite hash error
      }
    }

    // Step 9: Parse final JSON
    let originalDataObject: any;
    try {
      originalDataObject = JSON.parse(decryptedJsonString);
    } catch (parseError) {
      const errorMessage = parseError instanceof Error ? parseError.message : String(parseError);
      throw new Error(
        "Failed to parse decrypted content: " + errorMessage,
      );
    }

    // Step 10: Return data
    return originalDataObject;
  } catch (error) {
    // Ensure we propagate the error with its full detail
    decryptLogger.error("H2FIG Decryption failed:", error);
    throw error;
  }
}
